<template>
  <!-- Desktop version -->
  <template v-if="!isMobile">
    <div
      v-if="visible"
      class="comment-popover"
      :style="popoverStyle"
      @mousedown.stop
    >
      <div class="popover-content">
        <div class="comments-list-container">
          <ul
            v-if="localComments && localComments.length > 0"
            class="comments-list"
          >
            <li
              v-for="(comment, index) in localComments"
              :key="comment.id || index"
              class="comment-item"
            >
              <div class="comment-avatar">
                <ImageWrapper :image-url="comment.avatar" />
              </div>
              <div class="comment-details">
                <div class="d-flex justify-space-between align-start">
                  <div class="comment-header">
                    <span class="comment-author">{{
                      comment.author || "Anonymous"
                    }}</span>
                    <span class="comment-date">{{
                      formatDate(comment.date)
                    }}</span>
                  </div>
                  <div
                    class="d-flex ga-2"
                    v-if="loggedInUserId && comment.userId === loggedInUserId"
                  >
                    <button
                      @click="startEdit(comment)"
                      class="action-button edit-button"
                      title="Edit comment"
                    >
                      Edit
                    </button>
                    <button
                      @click="requestDelete(comment)"
                      class="action-button delete-button"
                      title="Delete comment"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div
                  v-if="editingCommentId === comment.id"
                  class="comment-edit-area"
                >
                  <textarea
                    v-model="editedCommentText"
                    class="comment-textarea edit-textarea"
                    ref="editCommentTextareaRef"
                    aria-label="Edit comment"
                  ></textarea>
                  <div class="edit-actions">
                    <button
                      @click="submitEditComment(comment)"
                      class="edit-save-button"
                      :disabled="isSubmitting"
                    >
                      Save
                    </button>
                    <button @click="cancelEditComment" class="edit-cancel-button">
                      Cancel
                    </button>
                  </div>
                </div>
                <p v-else class="comment-text">{{ comment.text }}</p>
                <div class="comment-actions">
                  <button
                    class="like-button"
                    :class="{ liked: comment.isLiked }"
                    @click="toggleLike(comment)"
                    :disabled="!isAuthenticated || isLikeLoading[comment.id]"
                    :title="
                      isAuthenticated
                        ? comment.isLiked
                          ? 'Unlike'
                          : 'Like'
                        : 'Sign In to like comments'
                    "
                    :aria-label="
                      isAuthenticated
                        ? comment.isLiked
                          ? 'Unlike'
                          : 'Like'
                        : 'Sign In to like comments'
                    "
                  >
                    <span
                      v-if="isLikeLoading[comment.id]"
                      class="like-loading"
                    ></span>
                    <svg
                      v-else
                      class="heart-icon"
                      viewBox="0 0 24 24"
                      :fill="comment.isLiked ? 'currentColor' : 'none'"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path
                        d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                      ></path>
                    </svg>
                    <span>{{ comment.likeCount || 0 }}</span>
                  </button>
                </div>
              </div>
            </li>
          </ul>
          <p v-else class="no-comments">
            No comments yet. Be the first to comment!
          </p>
        </div>

        <div v-if="isAuthenticated" class="comment-input-area">
          <textarea
            v-model="newCommentText"
            placeholder="Write your comment here!"
            class="comment-textarea"
            ref="commentTextareaRef"
            :disabled="isSubmitting"
            aria-label="Write a new comment"
          ></textarea>
          <button
            @click="submitCommentHandler"
            class="submit-button"
            :disabled="isSubmitting || !newCommentText.trim()"
          >
            {{ isSubmitting ? "Posting..." : "Post Comment" }}
          </button>
        </div>
        <div v-else class="auth-prompt-area">
          <div class="auth-prompt-area-content">
            <img src="/images/icon/warning.svg" alt="Warning" />
            <strong>Please sign in to comment.</strong>
          </div>
          <p class="mt-3" style="font-size: 0.8rem">
            Only logged in users may post comments on the thread.
          </p>
          <BaseButton
            @click="redirectToLogin"
            variant="primary"
            style="height: 2.5rem"
          >
            Sign In
          </BaseButton>
        </div>
      </div>
    </div>
  </template>

  <!-- Mobile version -->
  <template v-else>
    <v-bottom-sheet
      :model-value="visible"
      @update:model-value="!$event && $emit('close')"
      class="mobile-comment-sheet"
    >
      <v-card class="comment-popover popover-content" @mousedown.stop>
        <div class="comments-list-container">
          <ul
            v-if="localComments && localComments.length > 0"
            class="comments-list"
          >
            <li
              v-for="(comment, index) in localComments"
              :key="comment.id || index"
              class="comment-item"
            >
              <div class="comment-avatar">
                <ImageWrapper :image-url="comment.avatar" />
              </div>
              <div class="comment-details">
                <div class="d-flex justify-space-between align-start">
                  <div class="comment-header">
                    <span class="comment-author">{{
                      comment.author || "Anonymous"
                    }}</span>
                    <span class="comment-date">{{
                      formatDate(comment.date)
                    }}</span>
                  </div>
                  <div
                    class="d-flex ga-2"
                    v-if="loggedInUserId && comment.userId === loggedInUserId"
                  >
                    <button
                      @click="startEdit(comment)"
                      class="action-button edit-button"
                      title="Edit comment"
                    >
                      Edit
                    </button>
                    <button
                      @click="requestDelete(comment)"
                      class="action-button delete-button"
                      title="Delete comment"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div
                  v-if="editingCommentId === comment.id"
                  class="comment-edit-area"
                >
                  <textarea
                    v-model="editedCommentText"
                    class="comment-textarea edit-textarea"
                    ref="editCommentTextareaRef"
                    aria-label="Edit comment"
                  ></textarea>
                  <div class="edit-actions">
                    <button
                      @click="submitEditComment(comment)"
                      class="edit-save-button"
                      :disabled="isSubmitting"
                    >
                      Save
                    </button>
                    <button @click="cancelEditComment" class="edit-cancel-button">
                      Cancel
                    </button>
                  </div>
                </div>
                <p v-else class="comment-text">{{ comment.text }}</p>
                <div class="comment-actions">
                  <button
                    class="like-button"
                    :class="{ liked: comment.isLiked }"
                    @click="toggleLike(comment)"
                    :disabled="!isAuthenticated || isLikeLoading[comment.id]"
                    :title="
                      isAuthenticated
                        ? comment.isLiked
                          ? 'Unlike'
                          : 'Like'
                        : 'Sign In to like comments'
                    "
                    :aria-label="
                      isAuthenticated
                        ? comment.isLiked
                          ? 'Unlike'
                          : 'Like'
                        : 'Sign In to like comments'
                    "
                  >
                    <span
                      v-if="isLikeLoading[comment.id]"
                      class="like-loading"
                    ></span>
                    <svg
                      v-else
                      class="heart-icon"
                      viewBox="0 0 24 24"
                      :fill="comment.isLiked ? 'currentColor' : 'none'"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path
                        d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                      ></path>
                    </svg>
                    <span>{{ comment.likeCount || 0 }}</span>
                  </button>
                </div>
              </div>
            </li>
          </ul>
          <p v-else class="no-comments">
            No comments yet. Be the first to comment!
          </p>
        </div>

        <div v-if="isAuthenticated" class="comment-input-area">
          <textarea
            v-model="newCommentText"
            placeholder="Write your comment here!"
            class="comment-textarea"
            ref="commentTextareaRef"
            :disabled="isSubmitting"
            aria-label="Write a new comment"
          ></textarea>
          <button
            @click="submitCommentHandler"
            class="submit-button"
            :disabled="isSubmitting || !newCommentText.trim()"
          >
            {{ isSubmitting ? "Posting..." : "Post Comment" }}
          </button>
        </div>
        <div v-else class="auth-prompt-area">
          <div class="auth-prompt-area-content">
            <img src="/images/icon/warning.svg" alt="Warning" />
            <strong>Please sign in to comment.</strong>
          </div>
          <p class="mt-3" style="font-size: 0.8rem">
            Only logged in users may post comments on the thread.
          </p>
          <BaseButton
            @click="redirectToLogin"
            variant="primary"
            style="height: 2.5rem"
          >
            Sign In
          </BaseButton>
        </div>
      </v-card>
    </v-bottom-sheet>
  </template>

  <!-- Delete confirmation modal -->
  <div v-if="showDeleteModal" class="delete-modal-overlay">
    <div class="delete-modal">
      <h3>Delete Comment</h3>
      <p>
        Are you sure you want to delete this comment? This action cannot be
        undone.
      </p>
      <div class="delete-modal-actions">
        <BaseButton variant="secondary" @click="cancelDelete"
          >Cancel</BaseButton
        >
        <BaseButton
          variant="danger"
          @click="confirmDelete"
          :loading="isSubmitting"
        >
          Delete
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars */
import {
  ref,
  watch,
  computed,
  nextTick,
  onMounted,
  defineProps,
  defineEmits,
} from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import ImageWrapper from "../common/ImageWrapper.vue";
import BaseButton from "../common/BaseButton.vue";

const props = defineProps({
  visible: Boolean,
  targetBlockId: String,
  existingComments: { type: Array, default: () => [] },
  lessonId: [Number, String],
  position: {
    type: Object,
    default: () => ({ top: 0, left: 0, arrowSide: "top", arrowOffset: "50%" }),
  },
  commentService: {
    type: Object,
    required: true,
    validator: (service) => {
      return (
        typeof service.getContentComments === "function" &&
        typeof service.createContentComment === "function" &&
        typeof service.updateContentComment === "function" &&
        typeof service.deleteContentComment === "function" &&
        typeof service.likeContentComment === "function"
      );
    },
  },
});

const store = useStore();
const { mobile } = useDisplay();
const isMobile = computed(() => mobile.value);
const isAuthenticated = computed(() => !!store.state.user);

const emit = defineEmits(["close", "comments-updated"]);

const route = useRoute();
const router = useRouter();

const newCommentText = ref("");
const commentTextareaRef = ref(null);
const editingCommentId = ref(null);
const editedCommentText = ref("");
const editCommentTextareaRef = ref(null);
const isLikeLoading = ref({});
const isSubmitting = ref(false);
const showDeleteModal = ref(false);
const commentToDelete = ref(null);

const localComments = ref([]);

const loggedInUserId = computed(() => store.state.user?.user_id);

const popoverStyle = computed(() => {
  let styles = {};
  if (props.position.top !== undefined) styles.top = `${props.position.top}px`;
  if (props.position.left !== undefined)
    styles.left = `${props.position.left}px`;
  if (props.position.right !== undefined)
    styles.right = `${props.position.right}px`;
  if (props.position.bottom !== undefined)
    styles.bottom = `${props.position.bottom}px`;
  return styles;
});

watch(
  () => props.existingComments,
  (newComments) => {
    localComments.value = [...newComments];
  },
  { immediate: true, deep: true }
);

watch(
  () => props.visible,
  (newValue) => {
    console.log("Call from watch");
    if (newValue) {
      newCommentText.value = "";
      localComments.value = [...props.existingComments];
      nextTick(() => {
        if (commentTextareaRef.value) {
          commentTextareaRef.value.focus();
        }
      });
    }
  }
);

const toggleLike = async (comment) => {
  if (!isAuthenticated.value) return;

  // Set loading state for this specific comment
  isLikeLoading.value = { ...isLikeLoading.value, [comment.id]: true };

  try {
    const response = await props.commentService.likeContentComment(comment.id);
    if (response.status === 200) {
      // Toggle the liked status locally
      comment.isLiked = !comment.isLiked;

      // Adjust like count based on the action
      comment.likeCount = comment.isLiked
        ? (comment.likeCount || 0) + 1
        : Math.max(0, (comment.likeCount || 0) - 1);

      // Emit to parent that comments were updated
      emit("comments-updated");
    }
  } catch (err) {
    console.error("Error toggling like:", err);
  } finally {
    // Clear loading state for this comment
    const updatedLoadingState = { ...isLikeLoading.value };
    delete updatedLoadingState[comment.id];
    isLikeLoading.value = updatedLoadingState;
  }
};

const handleSaveComment = async () => {
  if (!props.lessonId) {
    console.error("Cannot save comment: Lesson ID not available.");
    return;
  }
  if (!props.targetBlockId) {
    console.error("Cannot save comment: Block ID not available.");
    return;
  }
  if (newCommentText.value.trim() === "") return;

  isSubmitting.value = true;

  const payload = {
    comment: newCommentText.value.trim(),
    lessonId: props.lessonId,
    blockId: props.targetBlockId,
  };
  try {
    const response = await props.commentService.createContentComment(payload);
    if (
      response.status === 201 ||
      (response.data &&
        response.data.message === "Comment created successfully")
    ) {
      // Add the new comment to local state
      const newComment = {
        id: response.data.data?.id || Date.now(), // Use API response ID or fallback
        text: newCommentText.value.trim(),
        author: store.state.user?.fullname || "You",
        avatar: store.state.user?.image || null,
        date: new Date().toISOString(),
        userId: store.state.user?.user_id,
        isLiked: false,
        likeCount: 0,
      };

      localComments.value.push(newComment);
      newCommentText.value = "";

      // Emit to parent that comments were updated
      emit("comments-updated");
    } else {
      console.error(
        "Failed to save comment:",
        response.data
          ? response.data.error || response.data.message
          : "Unknown error"
      );
    }
  } catch (error) {
    console.error(
      "Error saving comment:",
      error.response ? error.response.data : error.message
    );
  } finally {
    isSubmitting.value = false;
  }
};

const handleEditComment = async (comment) => {
  if (!props.lessonId) {
    console.error("Cannot edit comment: Lesson ID not available.");
    return;
  }
  if (!comment.id) {
    console.error("Cannot edit comment: Comment ID not available.");
    return;
  }
  if (!props.targetBlockId) {
    console.error("Cannot edit comment: Block ID not available.");
    return;
  }

  isSubmitting.value = true;

  const payload = {
    comment: editedCommentText.value.trim(),
    lessonId: props.lessonId,
    blockId: props.targetBlockId,
  };

  try {
    const response = await props.commentService.updateContentComment(
      comment.id,
      payload
    );
    if (
      response.status === 200 ||
      (response.data &&
        response.data.message === "Comment updated successfully")
    ) {
      // Update the comment in local state
      const commentIndex = localComments.value.findIndex(
        (c) => c.id === comment.id
      );
      if (commentIndex !== -1) {
        localComments.value[commentIndex].text = editedCommentText.value.trim();
      }

      cancelEditComment();

      // Emit to parent that comments were updated
      emit("comments-updated");
    } else {
      console.error(
        "Failed to update comment:",
        response.data
          ? response.data.error || response.data.message
          : "Unknown error"
      );
    }
  } catch (error) {
    console.error(
      "Error updating comment:",
      error.response ? error.response.data : error.message
    );
  } finally {
    isSubmitting.value = false;
  }
};

const handleDeleteComment = async (comment) => {
  if (!comment.id) {
    console.error("Cannot delete comment: Comment ID not available.");
    return;
  }

  isSubmitting.value = true;

  try {
    const response = await props.commentService.deleteContentComment(
      comment.id
    );
    if (
      response.status === 200 ||
      (response.data &&
        response.data.message === "Comment deleted successfully")
    ) {
      // Remove the comment from local state
      localComments.value = localComments.value.filter(
        (c) => c.id !== comment.id
      );

      // Emit to parent that comments were updated
      emit("comments-updated");
    } else {
      console.error(
        "Failed to delete comment:",
        response.data
          ? response.data.error || response.data.message
          : "Unknown error"
      );
    }
  } catch (error) {
    console.error(
      "Error deleting comment:",
      error.response ? error.response.data : error.message
    );
  } finally {
    isSubmitting.value = false;
  }
};

const startEdit = (comment) => {
  editingCommentId.value = comment.id;
  editedCommentText.value = comment.text;
  nextTick(() => {
    if (editCommentTextareaRef.value[0]) {
      editCommentTextareaRef.value[0].focus();
    }
  });
};

const cancelEditComment = () => {
  editingCommentId.value = null;
  editedCommentText.value = "";
};

const submitEditComment = (comment) => {
  if (editedCommentText.value.trim() === "") return;
  handleEditComment(comment);
};

const requestDelete = (comment) => {
  commentToDelete.value = comment;
  showDeleteModal.value = true;
};

const cancelDelete = () => {
  commentToDelete.value = null;
  showDeleteModal.value = false;
};

const confirmDelete = () => {
  if (commentToDelete.value) {
    handleDeleteComment(commentToDelete.value);
    cancelDelete();
  }
};

const submitCommentHandler = () => {
  handleSaveComment();
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffSeconds = Math.round((now - date) / 1000);
    const diffMinutes = Math.round(diffSeconds / 60);
    const diffHours = Math.round(diffMinutes / 60);
    const diffDays = Math.round(diffHours / 24);

    if (diffSeconds < 60) return `${diffSeconds} seconds ago`;
    if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return `1 day ago`;
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return "Invalid date";
  }
};

const redirectToLogin = () => {
  router.push({
    name: "LoginPage",
    query: { redirect: route.path },
  });
};

onMounted(() => {
  if (props.visible && commentTextareaRef.value) {
    commentTextareaRef.value.focus();
  }
});
</script>

<style scoped>
.comment-popover {
  position: absolute;
  z-index: 1000;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 350px;
  max-height: 450px;
  display: flex;
  flex-direction: column;
  font-size: var(--fs-small);
  font-weight: normal;
  color: #333;
  line-height: 1.5;
  text-align: left;
}

@media (max-width: 768px) {
  .comment-popover {
    position: relative;
    width: 100%;
    max-width: 100%;
    max-height: 85vh;
    border-radius: 16px 16px 0 0;
  }
}

.popover-arrow {
  position: absolute;
  width: 20px;
  height: 20px;
  background-color: white;
  z-index: -1;
  border: 1px solid #efefef;
  border-bottom-color: transparent;
  border-right-color: transparent;
}

.popover-content {
  padding: 15px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-grow: 1;
}

.comments-list-container {
  flex-grow: 1;
  overflow-y: auto;
  margin-bottom: 15px;
  padding-right: 5px;
}

.comments-list-container::-webkit-scrollbar {
  width: 6px;
}

.comments-list-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.comments-list-container::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}

.comments-list-container::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}

.comments-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.comment-item {
  display: flex;
  padding: 10px 0;
  border-bottom: 1px solid #efefef;
}

.comment-item:last-child {
  border-bottom: none;
}

.comment-avatar img {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-right: 12px;
}

.comment-details {
  flex-grow: 1;
}

.comment-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 4px;
}

.comment-author {
  font-weight: 600;
  font-size: 0.9em;
  color: #333;
}

.comment-date {
  font-size: 0.75em;
  color: #888;
}

.comment-text {
  font-size: 0.9em;
  color: #555;
  line-height: 1.5;
  white-space: pre-wrap;
  margin: 0 0 8px 0;
}

.comment-edit-area {
  margin-bottom: 8px;
}

.edit-textarea {
  min-height: 50px;
  margin-bottom: 8px;
  font-size: 0.9em;
  /* Match comment-text font-size */
}

.edit-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.edit-save-button,
.edit-cancel-button {
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85em;
  border: 1px solid transparent;
}

.edit-save-button {
  background-color: var(--primary-blue, #007bff);
  color: white;
}

.edit-save-button:hover {
  background-color: var(--primary-blue-dark, #0056b3);
}

.edit-cancel-button {
  background-color: #f0f0f0;
  color: #333;
  border-color: #ccc;
}

.edit-cancel-button:hover {
  background-color: #e0e0e0;
}

.comment-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  /* Add gap for action buttons */
}

.like-button {
  background: none;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #555;
  font-size: 0.8em;
  border-radius: 16px;
  transition: all 0.2s ease;
}

.like-button:hover:not(:disabled) {
  background-color: rgba(0, 0, 0, 0.05);
}

.like-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.like-button.liked {
  color: var(--primary-pink, #ff5a5f);
}

.heart-icon {
  width: 16px;
  height: 16px;
}

.like-loading {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.action-button {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.action-button:hover {
  opacity: 0.8;
}

.edit-button {
  color: var(--primary-blue, #007bff);
  font-size: 0.8rem;
}

.delete-button {
  color: #dc3545;
  font-size: 0.8rem;
}

.no-comments {
  text-align: center;
  color: #888;
  font-size: 0.9em;
  padding: 20px 0;
}

.comment-input-area {
  padding-top: 10px;
  /* border-top: 1px solid #efefef; */
  /* Removed top border as per image */
}

.comment-textarea {
  width: 100%;
  min-height: 60px;
  max-height: 120px;
  /* Limit growth */
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.9em;
  box-sizing: border-box;
  resize: vertical;
  margin-bottom: 10px;
}

.comment-textarea:focus {
  outline: none;
  border-color: var(--primary-blue, #007bff);
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
}

.submit-button {
  display: block;
  /* Make it full width or align right */
  width: 100%;
  padding: 10px 15px;
  background-color: var(--primary-blue, #007bff);
  /* Use a theme color */
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.95em;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.submit-button:hover {
  background-color: var(--primary-blue-dark, #0056b3);
  /* Darken on hover */
}

/* Authentication Prompt Area */
.auth-prompt-area {
  padding: 20px 15px;
  text-align: left;
  border-top: 1px solid #efefef;
  margin-top: 15px;
}

.auth-prompt-area-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.auth-prompt-area p {
  font-size: 0.9em;
  color: #555;
  line-height: 1.5;
  margin-bottom: 15px;
}

.login-button {
  display: inline-block;
  padding: 10px 20px;
  background-color: var(--primary-green, #28a745);
  /* Example: Green for login */
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.95em;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  /* If it's an <a> tag, or if you want to ensure no underline */
  transition: background-color 0.2s ease;
}

.login-button:hover {
  background-color: var(--primary-green-dark, #1e7e34);
  /* Darken on hover */
}

/* Delete confirmation modal */
.delete-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  /* Higher than popover z-index */
}

.delete-modal {
  background-color: white;
  border-radius: 16px;
  padding: 30px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.delete-modal h3 {
  color: var(--primary-pink, #ff5a5f);
  margin-top: 0;
  margin-bottom: 15px;
}

.delete-modal p {
  margin-bottom: 25px;
  color: #444;
}

.delete-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

@media (max-width: 768px) {
  .delete-modal {
    padding: 20px;
  }
}
</style>
